import type { PlayerColor } from '@/shared/board/types'
import type { Move, TurnState } from '@/shared/game/types'
import type { DiceRoller } from './dice'
import { allowedMoves } from './resolver'
import type { GameState } from './state'
import { tokensOf } from './state'
import { base, cornerAt } from './track'

/**
 * Ход одного игрока — порт App\Domain\Ludo\Turn.
 *
 * Расстановку фишек не хранит: она в GameState, и ход её не меняет, а
 * возвращает новую. Правила добросов целиком описаны в PHP-оригинале и
 * в docs/RULES.md; здесь только их исполнение.
 */
export type Turn = {
  readonly player: PlayerColor
  /** Нерасыгранные кубики текущего броска. */
  readonly dice: readonly number[]
  readonly bonus: number
  readonly finished: boolean
  /** Фишка ⇒ кубики, которыми она ходила с прошлого начисления добросов. */
  readonly moved: Readonly<Record<number, readonly number[]>>
  /** Броски с прошлого хода игрока, по одному на список. */
  readonly rolls: readonly (readonly number[])[]
  /** Ход ждёт, когда игрок бросит кубики: бросок — его действие. */
  readonly awaiting: boolean
}

/** Больше двух кубиков за раз не бросают: их всего два. */
const MAX_DICE = 2

/** Шестёрка: и выход из двора, и доброс. */
const SIX = 6

/**
 * Ещё один доброс — от бонуса на поле (режим с бонусами, docs/BONUSES.md).
 * Порт Turn::withExtraRoll: ложится в те же добросы, что и за угол.
 */
export function withExtraRoll(turn: Turn): Turn {
  return { ...turn, bonus: turn.bonus + 1 }
}

/** Начало хода: кубики ещё не брошены, ход ждёт игрока. */
export function beginTurn(player: PlayerColor): Turn {
  return { player, dice: [], bonus: 0, finished: false, moved: {}, rolls: [], awaiting: true }
}

/**
 * Бросить кубики.
 *
 * Первый бросок хода — всегда два кубика. Дальше бросается столько, сколько
 * заработано добросов, но не больше двух: лишний заработанный доброс
 * сгорает вместе с броском.
 */
export function rollTurn(turn: Turn, state: GameState, roller: DiceRoller): Turn {
  if (!turn.awaiting) return turn

  const count = turn.rolls.length === 0 ? MAX_DICE : Math.min(turn.bonus, MAX_DICE)
  const dice = Array.from({ length: count }, () => roller.roll())

  return settleTurn(
    {
      player: turn.player,
      dice,
      bonus: sixes(dice),
      finished: false,
      moved: turn.moved,
      rolls: [...turn.rolls, dice],
      awaiting: false,
    },
    state,
  )
}

export function allowedFor(turn: Turn, state: GameState): Move[] {
  if (turn.finished) return []

  return allowedMoves(state, turn.player, [...turn.dice])
}

/**
 * Ход сделан: кубик списывается, отметка «сходила» остаётся до подсчёта
 * углов. Состояние доски меняет вызывающий.
 */
export function playTurn(turn: Turn, move: Move): Turn {
  const dice = [...turn.dice]
  const at = dice.indexOf(move.die)

  if (at !== -1) dice.splice(at, 1)

  return {
    player: turn.player,
    dice,
    bonus: turn.bonus,
    finished: false,
    // Кубики фишки НАКАПЛИВАЮТСЯ: подавляет доброс за угол только шестёрка,
    // которой фишка дошла до угла одна (см. awardCorners).
    moved: { ...turn.moved, [move.token]: [...(turn.moved[move.token] ?? []), move.die] },
    rolls: turn.rolls,
    awaiting: false,
  }
}

/**
 * Довести ход до состояния, в котором либо есть что играть, либо ход ждёт
 * нового броска, либо он закончен. Сам не бросает: бросок — действие игрока.
 */
export function settleTurn(turn: Turn, state: GameState): Turn {
  if (turn.finished) return turn

  if (turn.dice.length > 0 && allowedFor(turn, state).length > 0) return turn

  // Кубики сыграны или сгорели — вот здесь и считаются углы.
  const awarded = awardCorners(turn, state)

  if (awarded.bonus < 1) {
    return { ...awarded, dice: [], bonus: 0, finished: true, awaiting: false }
  }

  return { ...awarded, dice: [], finished: false, awaiting: true }
}

/**
 * Начислить добросы за фишки, ПРИШЕДШИЕ в углы.
 *
 * Считаются только те, что ходили с прошлого начисления: стоящая в углу с
 * прошлого хода никуда не приходила, а прыгнувшая из угла в угол — приходила.
 */
function awardCorners(turn: Turn, state: GameState): Turn {
  let bonus = turn.bonus
  const tokens = tokensOf(state, turn.player)

  for (const [token, dice] of Object.entries(turn.moved)) {
    if (cornerAt(tokens[Number(token)] ?? base()) === null) continue

    // Дошла одними шестёрками — доброс за них начислен при броске. Шла
    // шестёркой И ещё кубиком — угол даёт свой, и порядок кубиков в ходе
    // «на сумму» ничего не решает.
    if (onlySixes(dice)) continue

    bonus++
  }

  return { ...turn, bonus, moved: {} }
}

/** Фишка шла только шестёрками? Пустой список — «нет»: см. PHP-оригинал. */
function onlySixes(dice: readonly number[]): boolean {
  return dice.length > 0 && dice.every((die) => die === SIX)
}

/**
 * Ход из localStorage.
 *
 * Партия за одним устройством переживает перезапуск браузера, а отметки
 * «фишка ходила» сменили форму: было «фишка ⇒ кубик», стало «фишка ⇒
 * кубики». Ход, сохранённый посреди партии в старом формате, иначе уронил бы
 * подсчёт углов — every у числа не существует.
 */
export function restoreTurn(turn: Turn): Turn {
  const moved: Record<number, readonly number[]> = {}

  for (const [token, used] of Object.entries(turn.moved as Record<number, unknown>)) {
    moved[Number(token)] = Array.isArray(used) ? (used as number[]) : [Number(used)]
  }

  return { ...turn, moved }
}

/** Ход в том виде, в каком его ждёт экран партии. */
export function turnView(turn: Turn): TurnState {
  return {
    player: turn.player,
    dice: [...turn.dice],
    bonus: turn.bonus,
    finished: turn.finished,
    awaiting: turn.awaiting,
    rolled: turn.rolls.length > 0,
  }
}

function sixes(dice: number[]): number {
  return dice.filter((die) => die === SIX).length
}
